import { ChevronDown, LogOut, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import { LangToggle } from "./LangToggle";
import { ThemeToggle } from "./ThemeToggle";
import { Popover, popoverItem } from "./ui";

export function UserMenu({
  name,
  email,
  onSignOut,
}: {
  name: string;
  email: string;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const t = useT();

  const initials =
    name
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  return (
    <Popover
      align="end"
      width="w-64"
      title={name}
      triggerClassName="flex items-center gap-1.5 rounded-full p-1 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
      trigger={({ open }) => (
        <>
          <span className="grid size-8 place-items-center rounded-full bg-accent/10 text-xs font-semibold text-accent dark:bg-accent/20">
            {initials}
          </span>
          <ChevronDown
            className={`size-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </>
      )}
    >
      {(close) => (
        <>
          <div className="border-b border-zinc-100 px-2.5 pb-2.5 pt-1.5 dark:border-zinc-800">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">{email}</p>
          </div>
          <div className="border-b border-zinc-100 py-1 dark:border-zinc-800">
            <button
              role="menuitem"
              onClick={() => {
                close();
                void navigate("/settings");
              }}
              className={popoverItem}
            >
              <Settings className="size-4 text-zinc-400" />
              {t.menu.settings}
            </button>
          </div>
          <div className="flex items-center justify-between px-2.5 py-2.5">
            <span className="text-sm text-zinc-500 dark:text-zinc-400">{t.menu.design}</span>
            <ThemeToggle />
          </div>
          <div className="flex items-center justify-between px-2.5 pb-2.5">
            <span className="text-sm text-zinc-500 dark:text-zinc-400">{t.menu.language}</span>
            <LangToggle />
          </div>
          <div className="border-t border-zinc-100 pt-1 dark:border-zinc-800">
            <button role="menuitem" onClick={onSignOut} className={popoverItem}>
              <LogOut className="size-4 text-zinc-400" />
              {t.menu.signOut}
            </button>
          </div>
        </>
      )}
    </Popover>
  );
}
